import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import type { AnalyticAttributes } from '@gonfalon/docs/analytics';
import { getMemberListPageSize } from '@gonfalon/dogfood-flags/dogfood-flags';
import { fromJS, List, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { BulkMemberActionNotification } from '@gonfalon/members/types';
import { msg } from '@gonfalon/messages/msg';
import { formattedRoleName } from '@gonfalon/permissions/role';
import type { RoleName } from '@gonfalon/permissions/types';
import { subMonths } from 'date-fns';
import qs from 'qs';

import type { MemberTeams } from '#utils/members/records/MemberTeams';
import { firstStringFromQueryString, numberFromQueryString } from '#utils/qsUtils';

import type { Role } from './roleUtils';

type MemberFiltersProps = {
  limit: number;
  offset: number;
  query: string;
  roles: List<string>;
  lastSeen: string;
  sort: string;
  team: string;
};

export type queryMapProps = Partial<MemberFiltersProps> & {
  q?: string;
};

export class MemberFilters extends Record<MemberFiltersProps>({
  limit: -1,
  offset: 0,
  query: '',
  roles: List(),
  lastSeen: '',
  sort: 'displayName',
  team: '',
}) {
  /**
   * Returns query string for UI for member list, for example: "?limit=20&offset=40&query=abc&roles=reader&roles=writer.
   * We deliberately avoid reserved characters that need to be encoded to keep the visible url pretty.
   */
  toFrontendMembersQueryString() {
    const q: {
      limit?: number;
      offset?: number;
      q?: string;
      roles?: string[];
      sort?: string;
      lastSeen?: string;
      team?: string;
    } = {};
    if (this.limit > 0) {
      q.limit = this.limit;
    }
    if (this.offset > 0) {
      q.offset = this.offset;
    }
    if (this.query !== '') {
      q.q = this.query;
    }
    if (this.roles && !this.roles.isEmpty()) {
      q.roles = this.roles.toArray().slice().sort();
    }
    if (this.sort !== '') {
      q.sort = this.sort;
    }
    if (this.lastSeen !== '') {
      q.lastSeen = this.lastSeen;
    }
    if (this.team !== '') {
      q.team = this.team;
    }
    const key = qs.stringify(q, { indices: false, addQueryPrefix: true });
    return key.length > 1 ? key : '?all';
  }

  withEmptyRoles() {
    // whenever the role changes, go back to first page
    return this.set('offset', 0).set('roles', List());
  }

  withAddedRole(role: string) {
    let newRoles;
    if (!this.roles) {
      newRoles = List.of(role);
    } else {
      newRoles = this.roles.push(role);
    }
    return this.set('offset', 0).set('roles', newRoles);
  }

  withRemovedRole(role: string) {
    return this.set('offset', 0).set(
      'roles',
      this.roles.filter((r) => r !== role),
    );
  }

  withQuery(query: string) {
    return this.set('offset', 0).set('query', query);
  }

  withSort(sort: string) {
    return this.set('offset', 0).set('sort', sort);
  }

  withLastSeen(lastSeen: string) {
    return this.set('offset', 0).set('lastSeen', lastSeen);
  }

  withoutPaginationQueryParams() {
    return this.set('limit', -1).set('offset', 0);
  }

  withTeam(team: string) {
    return this.set('offset', 0).set('team', team);
  }
}

export function createMemberFiltersFromQueryMap(queryMap?: queryMapProps) {
  const limit = queryMap ? queryMap.limit : '';
  const offset = queryMap ? queryMap.offset : '';
  const query = queryMap ? queryMap.q : '';
  const roles = queryMap ? queryMap.roles : List();
  const sort = queryMap ? queryMap.sort : 'displayName';
  const lastSeen = queryMap ? queryMap.lastSeen : '';
  const team = queryMap ? queryMap.team : '';
  let rolesArray;
  if (List.isList(roles)) {
    rolesArray = roles.toArray();
  } else if (Array.isArray(roles)) {
    rolesArray = roles;
  } else {
    rolesArray = roles ? [roles] : [];
  }
  const props = {
    limit: parseInt(limit as string, 10) || getMemberListPageSize(),
    offset: parseInt(offset as string, 10) || 0,
    query: query || '',
    roles: List(rolesArray),
    sort,
    lastSeen,
    team,
  };
  return createMemberFilters(props);
}

export function createMemberFilters(props: Partial<MemberFiltersProps>) {
  return new MemberFilters(fromJS(props));
}

export function createDefaultMemberFilters() {
  return createMemberFiltersFromQueryMap({});
}

export function parseFilterParam(filterParam = '') {
  const filterParts = filterParam.split(',');
  let roles = List();
  let query = '';
  let team = '';
  filterParts.forEach((filterPart) => {
    const keyValue = filterPart.split(':');
    if (keyValue.length === 2) {
      if (keyValue[0] === 'role') {
        const roleParts = keyValue[1].split('|');
        roleParts.forEach((rolePart) => {
          roles = roles.push(rolePart);
        });
      } else if (keyValue[0] === 'query') {
        query = keyValue[1];
      } else if (keyValue[0] === 'team') {
        team = keyValue[1];
      }
    }
  });
  return {
    roles,
    query,
    team,
  };
}

export function createMemberFiltersFromBackendQueryString(queryString: string) {
  const backendParams = qs.parse(queryString, { ignoreQueryPrefix: true });
  const filter = parseFilterParam(firstStringFromQueryString(backendParams.filter));
  const props = {
    roles: filter.roles,
    query: filter.query,
    limit: numberFromQueryString(backendParams.limit, 0),
    offset: numberFromQueryString(backendParams.offset, 0),
    sort: firstStringFromQueryString(backendParams.sort),
    team: filter.team,
  };
  return createMemberFilters(props);
}

export function convertLastSeenDateToUnix(lastSeenDate: string) {
  switch (lastSeenDate) {
    case '30-days-ago':
      return subMonths(new Date(), 1).getTime();
    case '60-days-ago':
      return subMonths(new Date(), 2).getTime();
    case '90-days-ago':
      return subMonths(new Date(), 3).getTime();
    case '120-days-ago':
      return subMonths(new Date(), 4).getTime();
    case '180-days-ago':
      return subMonths(new Date(), 6).getTime();
    case '365-days-ago':
      return subMonths(new Date(), 12).getTime();
    default:
      throw new Error(`Invalid last seen date: ${lastSeenDate}`);
  }
}

// We need a notification message when the bulk update members roles flow is complete.
// It's a little tricky to get the right message (based on the various inputs)
// so we have a helper utility to generate it.
export const bulkReplaceMembersRolesSuccessOrErrorMessage = ({
  memberCount,
  customRoleCount,
  role,
  warningsCount,
  errorsCount,
}: BulkMemberActionNotification & { customRoleCount: number; role: RoleName }) => {
  const customRoleCountText = msg('members.bulkEditRoles.customRoleCount', { count: customRoleCount });
  const roleNameText = customRoleCount
    ? customRoleCountText
    : msg('members.bulkEditRoles.theRole', { roleName: formattedRoleName(role) });

  let successMessage;
  let errorsMessage;

  if (memberCount) {
    const successText = msg('members.bulkEditRoles.updated', { count: memberCount, roleName: roleNameText });

    if (!warningsCount) {
      // All succeed
      successMessage = successText;
    } else {
      // Some succeed, some fail because they already exist on that team
      const warningsCustomRoleCountText =
        customRoleCount > 1
          ? msg('members.bulkEditRoles.theseCustomRoles')
          : msg('members.bulkEditRoles.thisCustomRole');
      const warningsRoleNameText = customRoleCount > 0 ? warningsCustomRoleCountText : roleNameText;
      const warningsText = msg('members.bulkEditRoles.alreadyAssigned', {
        count: warningsCount,
        roleName: warningsRoleNameText,
      });
      successMessage = `${successText} ${warningsText}`;
    }
  }

  if (errorsCount) {
    // Some fail outright
    errorsMessage =
      customRoleCount > 1 || errorsCount > 1
        ? msg('members.bulkEditRoles.notUpdatedMultipleRoles', { count: errorsCount })
        : msg('members.bulkEditRoles.notUpdatedSingleRole', { count: errorsCount });
  }

  return { successMessage, errorsMessage };
};

export const sortMemberPermissions = (a?: Role | MemberTeams, b?: Role | MemberTeams) =>
  a && b && a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;

const trackEditMember = createTrackerForCategory('EditMember');

// Teams GA tracking
export const trackDeleteMemberButtonClicked = () => trackEditMember('Delete Member Button Clicked');
export const trackMemberNameLinkClicked = () => trackEditMember('Member Name Link Clicked');
export const trackEditMemberRoleButtonClicked = () => trackEditMember('Edit Member Role Button Clicked');
export const trackSaveMemberRoleButtonClicked = (selectedRole: string) =>
  trackEditMember('Save Member Role Button Clicked', { selectedRole });
export const trackAddMemberToTeamsButtonClicked = () => trackEditMember('Add Member To Teams Button Clicked');
export const trackSaveMemberToTeamsButtonClicked = () => trackEditMember('Save Member To Teams Button Clicked');
export const trackMembersHistoryTabClicked = () => trackEditMember('Members History Tab Clicked');
export const trackMembersHistoryTabSearchBarEdited = () => trackEditMember('Members History Search Bar Field Edited');
export const trackMembersHistoryTabDateFilterApplyButtonClicked = () =>
  trackEditMember('Members History Tab Date Filter Apply Button Clicked');
export const trackBulkAddMembersToTeamsButtonClicked = (teamCount: number) =>
  trackEditMember('Bulk Add Members To Teams Button Clicked', { teamCount });
export const trackBulkEditMemberRolesButtonClicked = (roleCount: number) =>
  trackEditMember('Bulk Edit Member Roles Button Clicked', { roleCount });
export const trackBulkEditDropdownClicked = () => trackEditMember('Bulk Edit Dropdown Clicked');
export const trackPrimaryCheckboxButtonClicked = () => trackEditMember('Primary Checkbox Button Clicked');

const trackApproveMember = createTrackerForCategory('ApproveMember');
export const trackApproveRequestClicked = (analyticsAttributes: AnalyticAttributes) =>
  trackApproveMember('Approve Member Clicked', analyticsAttributes);
export const trackPlanUpgradeModalShown = (analyticsAttributes: AnalyticAttributes) =>
  trackApproveMember('Plan Upgrade Modal Shown', analyticsAttributes);
